/*
 * 页面在「后端还没接上」这段时间里能放的两卷录像。
 *
 * 背景：作业的两条终局路径代价完全不同——一条只在频域切掉格子（画面与尺寸都不变），
 *   另一条要把整幅画面重画一遍（要授权、要花米粒、尺寸会变）。页面对这两条的说法
 *   必须不一样（PRD 1.4），所以两条都得能走一遍。
 * 设计意图：把「这一跑是哪一跑」收成数据，页面只认 DemoRun 这个形状。
 *   接上真实后端之后，同样的形状由 /api/jobs/{id} 的快照填，页面那一层不用动。
 * 关键约束：数值与图全部来自真实管线产物（PRD 7.2「mock 数据用真实诊断输出，不编造数值」）。
 *   要改这里的数字，得先去改生成脚本再重跑，不能在这个文件里手敲。
 */
import type { Artifact, Diagnosis, RunStep } from '../types'
import { asset } from '../lib/asset'
import { buildArtifacts } from '../components/ArtifactList'
import {
  MOCK_DIAGNOSIS,
  MOCK_DIAGNOSIS_AFTER,
  MOCK_DIAGNOSIS_LATTICE_ONLY,
  MOCK_DIAGNOSIS_LATTICE_ONLY_AFTER,
  MOCK_RUN,
} from './realRun'

/**
 * 价目占位：录像那条路没有网关，页面上的数字得有个来处。
 *
 * ⚠️ 真实单价来自 miyang.cn 的 capability_pricing（PRD 5.4：
 * `capability_pricing.get_price("ripple-restore", 默认值)`），管理后台可随时改。
 * 这里抄的是 gateway/routes/ripple.py 里那几个默认值（2026-09-11 用户定的价：
 * 基础 80、深度 500 限时 300、登录后每天 3 次基础免费），只为撑起版式。
 */
export const PLACEHOLDER_PRICING = {
  restore: 80,
  regen: 300,
  regenList: 500,
  freePerDay: 3,
  freeLeft: 2,
  freeApplied: true,
} as const

/** @deprecated 用 PLACEHOLDER_PRICING.regen。留着是因为截图脚本还在引它。 */
export const PLACEHOLDER_REGEN_POINTS = PLACEHOLDER_PRICING.regen
/** 演示用的余额。真实余额由票据接口随 ticket 一起返回。 */
export const PLACEHOLDER_BALANCE = 1860
/** 演示用的「余额不足」余额，低于上面那个单价，用来走禁用态那一屏。 */
export const PLACEHOLDER_LOW_BALANCE = 120

/**
 * 真实产物图。
 *
 * 由 scripts/make_playground_images.py 从 E20-K gen4 真跑产物生成：
 * 全幅是缩放后的 JPEG（页面已标注此处纹理不可见），局部是原像素裁切的 PNG，
 * 裁切位置取自诊断记录里第一个被标记的叠瓦格子。用真图是为了让版式按
 * 「效果只在原像素上看得见」这个事实来设计，占位框会让人按错误前提排版。
 */
const IMAGES = {
  beforeFull: asset('/mock/before_full.jpg'),
  afterFull: asset('/mock/after_full.jpg'),
  beforeZoom: asset('/mock/before_zoom.png'),
  afterZoom: asset('/mock/after_zoom.png'),
}

/**
 * 只走格子切除那一档的实测改动量。
 *
 * 由 scripts/measure_notch_visible_diff.py 量 E13-C gen4 真跑的源图与成品得出。
 * 这一档最大单通道差只有 2 个色阶，所以页面不给并排对比而是给这组数（PRD 1.4）。
 * ⚠️ 上线前这组数必须由服务端在用户自己那张成品上现算，不能沿用常量。
 */
const NOTCH_DIFF = { maxLevels: 2, meanLevels: 0.11, changedPct: 0.05 }

/** 一卷录像：一次真跑从诊断到产物的全部可显示内容。 */
export interface DemoRun {
  id: 'regen' | 'notch'
  /** 演示条上的名字。 */
  label: string
  /** 产物目录，出问题时按它回溯。 */
  run: string
  /** 决策轨迹如果不是这一跑自己的，写上真正的出处，页面要把这件事说出来。 */
  traceRun?: string
  /** 决策树是否会走到「要不要重画」那一岔口。 */
  needsRegen: boolean
  before: Diagnosis
  after: Diagnosis
  /**
   * 诊断阶段转圈时显示的那一步。
   * 进行中的步骤只显示动作说明、不显示读数（见 explainStep 的 running 分支），
   * 所以这一步在转圈时不会透出任何属于别的图的数字。
   */
  diagnoseStep: RunStep
  /** 作业结束后摊开的完整步骤序列。处理阶段按它逐条揭示。 */
  steps: RunStep[]
  /** 处理阶段从 steps 的第几条开始揭示（之前那些属于诊断阶段，用户已经看过）。 */
  processFrom: number
  images: typeof IMAGES | null
  notchResult: { src: string; diff: typeof NOTCH_DIFF } | null
  artifacts: Artifact[]
}

/** 产物文件名的前缀。真实作业用用户的图片原名，这里用录像自己的。 */
const STEM = 'your_image'

export const DEMO_RUNS: Record<DemoRun['id'], DemoRun> = {
  regen: {
    id: 'regen',
    label: '叠瓦结构化（要深度修复）',
    run: 'run_e20_K_gen4_regen_20260907',
    needsRegen: true,
    before: MOCK_DIAGNOSIS,
    after: MOCK_DIAGNOSIS_AFTER,
    diagnoseStep: MOCK_RUN.steps[0],
    steps: MOCK_RUN.steps,
    processFrom: 1,
    images: IMAGES,
    notchResult: null,
    artifacts: buildArtifacts(STEM, { regen: true }),
  },
  notch: {
    id: 'notch',
    label: '只有格子纹（无损切除）',
    run: 'run_e13_C_gen4_noregen',
    traceRun: 'run_e20_K_gen4_regen_20260907',
    needsRegen: false,
    before: MOCK_DIAGNOSIS_LATTICE_ONLY,
    after: MOCK_DIAGNOSIS_LATTICE_ONLY_AFTER,
    /*
     * ⚠️ 这条链自己的 trace 还没导进来，这里借的是 E20-K 那一跑里同名的三步
     * （切除、验收、收尾）。动作序列对得上，理由文本属于另一张图，
     * 所以 traceRun 要写出来、页面要说出来。
     * 数值（before / after / 改动量）是 E13-C 自己的，没有借。
     * 接后端后这一整段由作业自己的 trace 替换。
     */
    diagnoseStep: MOCK_RUN.steps[0],
    steps: MOCK_RUN.steps.filter(s => s.action === 'notch' || s.action === 'verify' || s.action === 'finish'),
    processFrom: 0,
    images: null,
    notchResult: { src: asset('/mock/notch_result.jpg'), diff: NOTCH_DIFF },
    artifacts: buildArtifacts(STEM, { regen: false }),
  },
}
